import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import RichTextComposer from '../../components/RichTextComposer/RichTextComposerTiptap';
import { useAuth } from '../../contexts/AuthContext';
import { canUseAiScenes } from '../../utils/staffPermissions';
import universeService from '../../services/universeService';
import aiSceneService from '../../services/aiSceneService';
import styles from './AiScenes.module.css';

const EMPTY_FORM = {
  universeId: '',
  forumId: '',
  npcId: '',
  instructions: '',
};

const AiScenes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: sceneIdParam } = useParams();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [scenes, setScenes] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(10);
  const [dailyUsed, setDailyUsed] = useState(0);

  const [universes, setUniverses] = useState([]);
  const [forums, setForums] = useState([]);
  const [npcs, setNpcs] = useState([]);

  const [view, setView] = useState('list');
  const [form, setForm] = useState(EMPTY_FORM);
  const [scene, setScene] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [feedback, setFeedback] = useState('');

  const hasAccess = useMemo(() => canUseAiScenes(user), [user]);

  const loadList = useCallback(async () => {
    const data = await aiSceneService.list();
    setScenes(data.scenes || []);
    setDailyLimit(data.dailyLimit ?? 10);
    setDailyUsed(data.dailyUsed ?? 0);
  }, []);

  const loadScene = useCallback(async (id) => {
    const data = await aiSceneService.get(id);
    const nextScene = data.scene;
    setScene(nextScene);
    setDraftTitle(nextScene.generatedTitle || '');
    setDraftContent(nextScene.generatedContent || '');
    setView('draft');
  }, []);

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    const init = async () => {
      setLoading(true);
      setError('');
      try {
        const universesData = await universeService.getUniverses();
        setUniverses(universesData || []);
        await loadList();
        if (sceneIdParam) {
          await loadScene(sceneIdParam);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Impossible de charger les scènes IA.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [hasAccess, sceneIdParam, loadList, loadScene]);

  useEffect(() => {
    if (!form.universeId) {
      setForums([]);
      setNpcs([]);
      return;
    }

    const loadContext = async () => {
      try {
        const [forumsData, npcsData] = await Promise.all([
          aiSceneService.getRoleplayForums(form.universeId),
          aiSceneService.getSelectableNpcs(form.universeId),
        ]);
        setForums(forumsData.forums || []);
        setNpcs(npcsData.npcs || []);
      } catch {
        setForums([]);
        setNpcs([]);
      }
    };

    loadContext();
  }, [form.universeId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError('');
    try {
      const data = await aiSceneService.create({
        universeId: Number(form.universeId),
        forumId: Number(form.forumId),
        npcId: Number(form.npcId),
        instructions: form.instructions.trim(),
      });
      setScene(data.scene);
      setDraftTitle(data.scene.generatedTitle || data.generated?.title || '');
      setDraftContent(data.scene.generatedContent || data.generated?.html || '');
      setView('draft');
      await loadList();
      navigate(`/scenes-ia/${data.scene.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!scene) return;
    setSaving(true);
    setError('');
    try {
      const data = await aiSceneService.update(scene.id, {
        generatedTitle: draftTitle,
        generatedContent: draftContent,
      });
      setScene(data.scene);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (fullReset = false) => {
    if (!scene) return;
    setGenerating(true);
    setError('');
    try {
      const data = await aiSceneService.regenerate(scene.id, {
        feedback: feedback.trim() || undefined,
        fullReset,
      });
      setScene(data.scene);
      setDraftTitle(data.scene.generatedTitle || data.generated?.title || '');
      setDraftContent(data.scene.generatedContent || data.generated?.html || '');
      setFeedback('');
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la régénération.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!scene) return;
    setSaving(true);
    setError('');
    try {
      await aiSceneService.update(scene.id, {
        generatedTitle: draftTitle,
        generatedContent: draftContent,
      });
      const data = scene.draftMode === 'reply'
        ? await aiSceneService.publishReply(scene.id)
        : await aiSceneService.publish(scene.id);
      setScene(data.scene);
      setDraftTitle('');
      setDraftContent('');
      await loadList();
      if (data.thread?.slug) {
        navigate(`/threads/${data.thread.slug}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la publication.');
    } finally {
      setSaving(false);
    }
  };

  const handleReplyDraft = async (targetScene = scene) => {
    if (!targetScene) return;
    setGenerating(true);
    setError('');
    try {
      const data = await aiSceneService.replyDraft(targetScene.id, {
        feedback: feedback.trim() || undefined,
      });
      setScene(data.scene);
      setDraftTitle('');
      setDraftContent(data.scene.generatedContent || data.generated?.html || '');
      setFeedback('');
      setView('draft');
      await loadList();
      navigate(`/scenes-ia/${data.scene.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération de suite.');
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenReplyDraft = async (sceneId) => {
    setGenerating(true);
    setError('');
    try {
      const data = await aiSceneService.replyDraft(sceneId);
      setScene(data.scene);
      setDraftTitle('');
      setDraftContent(data.scene.generatedContent || data.generated?.html || '');
      setFeedback('');
      setView('draft');
      await loadList();
      navigate(`/scenes-ia/${data.scene.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération de suite.');
    } finally {
      setGenerating(false);
    }
  };

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    { name: 'Scènes IA', url: null, icon: 'category' },
  ];

  if (!hasAccess) {
    return (
      <Layout>
        <ErrorMessage message="Accès réservé aux modérateurs et animateurs." />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Loading message="Chargement des scènes IA..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.page}>
        <Breadcrumb items={breadcrumbItems} />

        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Scènes RP par IA</h1>
            <p className={styles.subtitle}>
              Génère des scènes jouées par des PNJ avec validation MJ avant publication.
            </p>
          </div>
          <div className={styles.quota}>
            Générations aujourd&apos;hui : {dailyUsed} / {dailyLimit}
          </div>
        </header>

        {error && <ErrorMessage message={error} onClose={() => setError('')} />}

        {view === 'list' && (
          <>
            <div className={styles.actionsBar}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => {
                  setView('create');
                  setForm(EMPTY_FORM);
                  setScene(null);
                  navigate('/scenes-ia');
                }}
              >
                Nouvelle scène
              </button>
            </div>

            <section className={styles.listSection}>
              <h2 className={styles.sectionTitle}>Mes scènes</h2>
              {scenes.length === 0 ? (
                <p className={styles.empty}>Aucune scène pour le moment.</p>
              ) : (
                <div className={styles.sceneList}>
                  {scenes.map((item) => (
                    <article key={item.id} className={styles.sceneCard}>
                      <div className={styles.sceneCardMain}>
                        <h3 className={styles.sceneCardTitle}>
                          {item.generatedTitle || item.thread?.title || `Scène #${item.id}`}
                        </h3>
                        <p className={styles.sceneCardMeta}>
                          {item.npc?.name} · {item.forum?.name} · {item.status}
                          {item.draftMode === 'reply' && item.generatedContent ? ' · brouillon de suite' : ''}
                        </p>
                      </div>
                      <div className={styles.sceneCardActions}>
                        {item.thread?.slug && (
                          <Link to={`/threads/${item.thread.slug}`} className={styles.linkButton}>
                            Voir le thread
                          </Link>
                        )}
                        {item.status === 'published' && !item.generatedContent && (
                          <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={() => handleOpenReplyDraft(item.id)}
                            disabled={generating}
                          >
                            Générer la suite
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          onClick={() => {
                            navigate(`/scenes-ia/${item.id}`);
                            loadScene(item.id);
                          }}
                        >
                          Ouvrir
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {view === 'create' && (
          <section className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Nouvelle scène</h2>
            <form onSubmit={handleCreate} className={styles.form}>
              <label className={styles.field}>
                <span>Univers</span>
                <select
                  value={form.universeId}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    universeId: e.target.value,
                    forumId: '',
                    npcId: '',
                  }))}
                  required
                >
                  <option value="">Choisir un univers</option>
                  {universes.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Forum RP</span>
                <select
                  value={form.forumId}
                  onChange={(e) => setForm((prev) => ({ ...prev, forumId: e.target.value }))}
                  required
                  disabled={!form.universeId}
                >
                  <option value="">Choisir un forum</option>
                  {forums.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>PNJ</span>
                <select
                  value={form.npcId}
                  onChange={(e) => setForm((prev) => ({ ...prev, npcId: e.target.value }))}
                  required
                  disabled={!form.universeId}
                >
                  <option value="">Choisir un PNJ validé</option>
                  {npcs.map((n) => (
                    <option key={n.id} value={n.id}>{n.name}</option>
                  ))}
                </select>
              </label>

              <p className={styles.hint}>
                Tu peux créer un PNJ depuis la fiche faction ou{' '}
                <Link to="/characters/npc/new">créer un PNJ</Link> puis le faire valider.
              </p>

              <label className={styles.field}>
                <span>Consignes MJ (objectifs, pistes, plans)</span>
                <textarea
                  value={form.instructions}
                  onChange={(e) => setForm((prev) => ({ ...prev, instructions: e.target.value }))}
                  rows={6}
                  required
                  placeholder="Ex. : Black Mask et son gang préparent une opération à Gotham. Le MJ veut une ambiance menaçante..."
                />
              </label>

              <div className={styles.formActions}>
                <button type="button" className={styles.secondaryButton} onClick={() => setView('list')}>
                  Annuler
                </button>
                <button type="submit" className={styles.primaryButton} disabled={generating}>
                  {generating ? 'Génération en cours...' : 'Générer la scène'}
                </button>
              </div>
            </form>
          </section>
        )}

        {view === 'draft' && scene && (
          <section className={styles.draftSection}>
            <div className={styles.draftHeader}>
              <div>
                <h2 className={styles.sectionTitle}>
                  {scene.draftMode === 'reply' ? 'Brouillon de réponse PNJ' : 'Brouillon de scène'}
                </h2>
                <p className={styles.sceneCardMeta}>
                  {scene.npc?.name} · {scene.forum?.name} · {scene.status}
                </p>
              </div>
              <button type="button" className={styles.secondaryButton} onClick={() => setView('list')}>
                Retour à la liste
              </button>
            </div>

            {scene.draftMode === 'opening' && (
              <label className={styles.field}>
                <span>Titre du thread</span>
                <input
                  type="text"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  className={styles.textInput}
                />
              </label>
            )}

            <div className={styles.composerWrap}>
              <span className={styles.composerLabel}>Message RP</span>
              <RichTextComposer value={draftContent} onChange={setDraftContent} />
            </div>

            <label className={styles.field}>
              <span>Feedback pour modification (optionnel)</span>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                placeholder="Ex. : Plus menaçant, moins de descriptions, mentionner le gang..."
              />
            </label>

            <div className={styles.formActions}>
              <button type="button" className={styles.secondaryButton} onClick={handleSaveDraft} disabled={saving}>
                {saving ? 'Sauvegarde...' : 'Sauvegarder le brouillon'}
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => handleRegenerate(false)}
                disabled={generating}
              >
                {generating ? 'Génération...' : 'Demander une modification'}
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => handleRegenerate(true)}
                disabled={generating}
              >
                Régénérer
              </button>
              {scene.status === 'published' && !scene.generatedContent && (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => handleReplyDraft(scene)}
                  disabled={generating}
                >
                  {generating ? 'Génération...' : 'Générer la suite'}
                </button>
              )}
              {scene.generatedContent && (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handlePublish}
                  disabled={saving || generating}
                >
                  {saving ? 'Publication...' : scene.draftMode === 'reply' ? 'Publier la réponse' : 'Publier le thread'}
                </button>
              )}
            </div>
          </section>
        )}

        {generating && (
          <div className={styles.generatingOverlay}>
            <Loading message="L'IA rédige la scène..." />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AiScenes;
